import React, { useEffect } from 'react'

import { Label } from '@pb/components'
import { isValidDateString, getLabelFromStorage } from '@pb/utils'
import { differenceInCalendarDays, subYears } from 'date-fns'
import { filter } from 'ramda'
import { useSelector, useDispatch } from 'react-redux'

import appSelectors from 'App/duck/selectors'
import { groupByProp } from 'helpers/cookbook'
import useFieldsConfigs from 'hooks/useFieldsConfigs'
import { Overrides } from 'hooks/useFieldsConfigs/types'

import { operationFetchProvinces } from '../duck/operations'
import selectors from '../duck/selectors'

export const useUpgradeToClubFieldsConfig = (): any => {
  const fieldsOrdering = useSelector(appSelectors.getFieldsOrdering)
  const fieldNamesInConfig = Object.values(fieldsOrdering.LOYALTY)

  const fieldsConfig = useSelector(appSelectors.getFieldsConfig)
  const isFetchingProvinces = useSelector(selectors.getIsProvincesLoading)
  const isProvincesPopulated = useSelector(selectors.getIsProvincesPopulated)
  const shouldFetchProvinces =
    fieldNamesInConfig.includes('province') && !isFetchingProvinces && !isProvincesPopulated
  const provinces = useSelector(selectors.getProvinces)
  const marketMinAge = useSelector(appSelectors.getMinAge)
  const isUserCBPMember = useSelector(appSelectors.getUserCBPMember)
  const CELL_PHONE_FIELD_NAME = 'cellPhone'

  const isCellPhoneRequired = fieldsConfig
    .filter((field) => field.type?.name === 'LOYALTY')
    .find((field) => field.required && field.attribute === CELL_PHONE_FIELD_NAME)

  const fieldsToRender = [
    ...(isCellPhoneRequired ? ['cellPhone'] : []),
    'dateOfBirth',
    ...(provinces ? ['province'] : []),
  ]
  const shouldRenderField = (field): any => fieldsToRender.includes(field.attribute)

  const dispatch = useDispatch()

  const isUserOfAge = (dateOfBirth): any => {
    const minDateOfBirth = subYears(new Date(), marketMinAge)
    const currentDateOfBirth = new Date(dateOfBirth)
    return (
      differenceInCalendarDays(minDateOfBirth, currentDateOfBirth) >= 0 || (
        <Label copyKey="checkout.home.address.birthdate.minage" replacements={marketMinAge} />
      )
    )
  }

  // TODO: Order new "correct" label keys for CUSTOMER, so we don't have to use "error.missing.text" for placeholder and CHECKOUTCUSTOMER..
  const isValidDate = (dateOfBirth): any =>
    isValidDateString(dateOfBirth) || getLabelFromStorage('checkout.home.address.birthdate.invalid')

  useEffect(() => {
    if (shouldFetchProvinces) {
      dispatch(operationFetchProvinces())
    }
  }, [shouldFetchProvinces])

  const selectOptions: { label: string; value: string }[] = []

  if (provinces) {
    provinces.forEach((province) => {
      selectOptions.push({
        label: province.name,
        value: province.name,
      })
    })
  }

  const overrides = fieldNamesInConfig.map((name) => ({
    attribute: name,
    label: `CUSTOMER.${name}.label.text`,
    errorMessages: {
      pattern: `CUSTOMER.${name}.error.regex.text`,
      required: `CUSTOMER.${name}.error.missing.text`,
    },
    ...(name === CELL_PHONE_FIELD_NAME && {
      label: 'CHECKOUTCUSTOMER.cellPhone.label.text',
    }),
    ...(name === 'province' && {
      placeholder: getLabelFromStorage('CUSTOMER.province.error.missing.text'),
      selectOptions,
    }),
    ...(name === 'dateOfBirth' && {
      disabled: isUserCBPMember,
      validation: {
        validate: {
          isValidDate,
          isUserOfAge,
        },
      },
    }),
  }))

  const groupedOverrides = groupByProp('attribute')(overrides) as Overrides

  const { fieldsInOrder } = useFieldsConfigs('LOYALTY', groupedOverrides)

  return filter(shouldRenderField, fieldsInOrder)
}
